import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';

import { CheckIcon, ChevronDownIcon, ClockArrowRightIcon, ClockIcon, XIcon } from '@air/design-icons';
import { DropdownMenu, DropdownMenuItem } from '@air/primitive-dropdown-menu';
import { Tooltip } from '@air/primitive-tooltip';

import { type CommentTimeMode } from '~/components/AnnotationsV2/hooks/useCommentTimeMode';
import { MENTIONS_TIMESTAMP_PILL, TIMESTAMP_INPUT_BUTTON } from '~/constants/testIDs';

export type TimestampButtonProps = {
  shouldEnableTimestamps: boolean;
  setShouldEnableTimestamps: (shouldEnableTimestamps: boolean) => void;
  formattedTimestamp?: string;
  shouldDisableTimestampsAndAnnotations: boolean;
  timeMode?: CommentTimeMode;
  onSelectTimeMode?: (mode: CommentTimeMode) => void;
  canSelectTimeRange?: boolean;
};

export const TimestampButton = memo(
  ({
    shouldEnableTimestamps,
    setShouldEnableTimestamps,
    formattedTimestamp,
    shouldDisableTimestampsAndAnnotations,
    timeMode,
    onSelectTimeMode,
    canSelectTimeRange = true,
  }: TimestampButtonProps) => {
    const tooltipLabel = shouldDisableTimestampsAndAnnotations
      ? 'Timestamp commenting is disabled during video processing'
      : 'Timestamp';
    const isMenuVariant = !!onSelectTimeMode && timeMode !== undefined;
    const isActive = isMenuVariant ? timeMode !== 'none' : shouldEnableTimestamps;
    const isRangeMode = canSelectTimeRange && timeMode === 'range';
    const PillIcon = isRangeMode ? ClockArrowRightIcon : ClockIcon;
    const buttonLabel =
      isActive && formattedTimestamp
        ? `${isRangeMode ? 'Time range' : 'Timestamp'} ${formattedTimestamp}`
        : tooltipLabel;

    const handleToggleClick = useCallback(() => {
      setShouldEnableTimestamps(!shouldEnableTimestamps);
    }, [setShouldEnableTimestamps, shouldEnableTimestamps]);

    const handleSelectRange = useCallback(() => {
      onSelectTimeMode?.('range');
    }, [onSelectTimeMode]);

    const handleSelectTimestamp = useCallback(() => {
      onSelectTimeMode?.('timestamp');
    }, [onSelectTimeMode]);

    const handleSelectNone = useCallback(() => {
      onSelectTimeMode?.('none');
    }, [onSelectTimeMode]);

    const pill = useMemo(
      () => (
        <button
          type="button"
          aria-label={buttonLabel}
          disabled={shouldDisableTimestampsAndAnnotations}
          title={isMenuVariant ? tooltipLabel : undefined}
          className={classNames('flex items-center rounded-4 px-1 py-0.5 tabular-nums', {
            'ads:bg-ads-ember-7 bg-orange-7 ads:hover:bg-ads-ember-8 hover:bg-orange-8': isActive,
            'ads:hover:bg-hover-subtle hover:bg-gray-4 active:bg-gray-4 ads:active:bg-hover-subtle':
              !isActive && !shouldDisableTimestampsAndAnnotations,
          })}
          onClick={isMenuVariant ? undefined : handleToggleClick}
        >
          <PillIcon
            className={classNames('size-3 p-0 hover:bg-transparent', isActive ? '' : 'h-[18px]', {
              'ads:text-icon-emphasis text-gray-12': isActive,
              'ads:text-icon-subtle text-gray-8': !isActive && shouldDisableTimestampsAndAnnotations,
              'ads:text-icon-default text-gray-11': !isActive && !shouldDisableTimestampsAndAnnotations,
            })}
            data-testid={TIMESTAMP_INPUT_BUTTON}
          />
          {isActive && (
            <>
              <div
                className="ml-1 font-medium ads:text-primary text-12 text-gray-12 leading-[18px]"
                data-testid={MENTIONS_TIMESTAMP_PILL}
              >
                {formattedTimestamp}
              </div>
              {isMenuVariant ? (
                <ChevronDownIcon size="xs" className="mr-0.5 ml-1 ads:text-icon-emphasis text-gray-12" />
              ) : (
                <XIcon size="xs" className="mr-0.5 ml-1 ads:text-icon-emphasis text-gray-12" />
              )}
            </>
          )}
        </button>
      ),
      [
        PillIcon,
        buttonLabel,
        formattedTimestamp,
        handleToggleClick,
        isActive,
        isMenuVariant,
        shouldDisableTimestampsAndAnnotations,
        tooltipLabel,
      ],
    );

    if (isMenuVariant) {
      return (
        <DropdownMenu align="start" disabled={shouldDisableTimestampsAndAnnotations} side="top" trigger={pill}>
          {canSelectTimeRange ? (
            <DropdownMenuItem
              label="Time Range"
              onSelect={handleSelectRange}
              suffix={
                timeMode === 'range' ? (
                  <CheckIcon size="sm" className="ads:text-icon-emphasis text-gray-12" />
                ) : undefined
              }
            />
          ) : null}
          <DropdownMenuItem
            label="Timestamp"
            onSelect={handleSelectTimestamp}
            suffix={
              timeMode === 'timestamp' ? (
                <CheckIcon size="sm" className="ads:text-icon-emphasis text-gray-12" />
              ) : undefined
            }
          />
          <DropdownMenuItem
            label="None"
            onSelect={handleSelectNone}
            suffix={
              timeMode === 'none' ? <CheckIcon size="sm" className="ads:text-icon-emphasis text-gray-12" /> : undefined
            }
          />
        </DropdownMenu>
      );
    }

    return (
      <Tooltip label={tooltipLabel} side="top">
        {pill}
      </Tooltip>
    );
  },
);

TimestampButton.displayName = 'TimestampButton';
